import React from 'react';
import './comic.css';

// Small stroke-based line icons (Feather-style, matching IconButton.jsx's
// existing convention), one per tab, each a plain, undistorted 24x24 glyph
// -- no stretching/scaling tricks, unlike the brief mascot-image-based
// version this replaces (which needed an asymmetric mask-size zoom that
// distorted the source art's proportions to fit a small icon slot).
const ICONS = {
  routines: <polyline points="20 6 9 17 4 12" />,
  quickstart: <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />,
  exercises: (
    <>
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
    </>
  ),
  // Hourglass, per direct user request to use an hourglass for every
  // "Previously.../history" icon app-wide -- matches IconButton.jsx's
  // `info` icon (the per-exercise "view history" button).
  history: <polyline points="6 4 18 4 12 12 18 20 6 20 12 12 6 4" />,
  shop: (
    <>
      <circle cx="9" cy="21" r="1" />
      <circle cx="20" cy="21" r="1" />
      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
    </>
  )
};

// Labels double as accessibility text (aria-label/title) now that the bar
// is icon-only, and as a dev identifier -- each tab's full name still
// appears as its own CategoryPanel title once selected.
const TABS = [
  { key: "routines", label: "Select Mission" },
  { key: "quickstart", label: "Quick Start" },
  { key: "exercises", label: "Manage My Exercises" },
  { key: "history", label: "Previously..." },
  { key: "shop", label: "Shop" }
];

/**
 * Fixed bottom tab bar -- the app's primary navigation as of the move away
 * from stacked, individually-collapsible panels (2026-08-18) to one
 * native-iOS-style bar with 5 tabs, matching the always-visible-at-the-
 * bottom convention a real UITabBarController uses (this app is being
 * wrapped for iOS via Capacitor, so it's worth feeling native).
 * [2026-08-18] Reverted from small mascot-image copies (tried right before
 * this) back to a self-contained vector icon set, per direct request --
 * a checkmark, lightning bolt, the same list glyph, a circular
 * counter-clockwise arrow, and a shopping cart. `stroke="currentColor"` on
 * the shared <svg> wrapper means color (cream at rest, green when active)
 * comes entirely from `.comic-tab-bar__btn`'s own `color`, no extra CSS
 * needed per icon.
 */
const TabBar = ({ activeTab, onSelect }) => (
  <nav className="comic-tab-bar">
    {TABS.map((tab) => {
      const isActive = activeTab === tab.key;
      return (
        <button
          key={tab.key}
          type="button"
          className={`comic-tab-bar__btn${isActive ? " comic-tab-bar__btn--active" : ""}`}
          onClick={() => onSelect(tab.key)}
          aria-current={isActive ? "page" : undefined}
          aria-label={tab.label}
          title={tab.label}
          data-tour={`tab-${tab.key}`}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            {ICONS[tab.key]}
          </svg>
        </button>
      );
    })}
  </nav>
);

export default TabBar;
